// frontend/src/components/editor/TipTapEditor.tsx
import React, { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import ImageExtension from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { TextAlign } from '@tiptap/extension-text-align';
import { Underline } from '@tiptap/extension-underline';
import { Link } from '@tiptap/extension-link';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import { Highlight } from '@tiptap/extension-highlight';
import { FontFamily } from '@tiptap/extension-font-family';
import { ContentBlock } from '../../types';
import { blocksToHtml, parseHtmlToBlocks } from '../../utils/contentBlocks';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const API_BASE = process.env.REACT_APP_API_URL ?? '';

/** Prepend API_BASE to relative image paths */
function resolveImageUrl(src: string): string {
  if (!src) return '';
  if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('data:')) return src;
  return `${API_BASE}/${src.replace(/^\//, '')}`;
}

// ---------------------------------------------------------------------------
// data-block-id injection
// ---------------------------------------------------------------------------

const BLOCK_TAGS = new Set(['p', 'img', 'table', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6']);

function injectBlockIds(editorDom: Element, blocks: ContentBlock[]) {
  const children = editorDom.children;
  let blockIdx = 0;
  for (let i = 0; i < children.length; i++) {
    const child = children[i] as HTMLElement;
    const tag = child.tagName?.toLowerCase();
    // Skip tableWrapper divs — they are ProseMirror wrappers, not content blocks
    if (tag === 'div' && child.classList.contains('tableWrapper')) continue;
    if (BLOCK_TAGS.has(tag)) {
      if (blockIdx < blocks.length) {
        child.setAttribute('data-block-id', blocks[blockIdx].id);
        child.setAttribute('data-block-type', blocks[blockIdx].type);
        blockIdx++;
      }
    }
  }
}

// ---------------------------------------------------------------------------
// TipTapEditor
// ---------------------------------------------------------------------------

export interface TipTapEditorHandle {
  insertImage: (url: string, alt?: string) => void;
}

interface TipTapEditorProps {
  blocks: ContentBlock[];
  activeChapterId?: string | null;
  onContentChange?: (blocks: ContentBlock[]) => void;
  onBlockAction?: (blockId: string, action: string) => void;
  onImageInsert?: () => void;
  isGenerating?: boolean;
  readOnly?: boolean;
}

const TipTapEditor = forwardRef<TipTapEditorHandle, TipTapEditorProps>(({
  blocks,
  activeChapterId,
  onContentChange,
  onBlockAction,
  onImageInsert,
  isGenerating = false,
  readOnly = false,
}, ref) => {
  // Resolve image URLs before passing to TipTap
  const resolvedHtml = (() => {
    if (blocks.length === 0) return '<p></p>';
    // blocksToHtml produces <img src="..."> — resolve relative paths
    let html = blocksToHtml(blocks);
    html = html.replace(/<img\s+src="([^"]+)"/g, (_m, src) => {
      return `<img src="${resolveImageUrl(src)}"`;
    });
    return html;
  })();

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      ImageExtension.configure({ allowBase64: true }),
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      FontFamily,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { target: '_blank' } }),
    ],
    content: resolvedHtml,
    editable: !readOnly && !isGenerating,
    onCreate: ({ editor }) => {
      injectBlockIds(editor.view.dom, blocks);
    },
    onUpdate: ({ editor }) => {
      // Serialize editor HTML back to ContentBlock[] for saving
      if (!onContentChange) return;
      const html = editor.getHTML();
      const parsed = parseHtmlToBlocks(html);
      // Preserve block IDs and metadata from original blocks where possible
      const origBlocks = blocksRef.current;
      const merged = parsed.map((newBlock, i) => {
        const orig = origBlocks[i];
        if (orig && orig.type === newBlock.type) {
          return { ...newBlock, id: orig.id, meta: orig.meta };
        }
        return newBlock;
      });
      onContentChange(merged);
    },
  });

  const injectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const blocksRef = useRef(blocks);
  blocksRef.current = blocks;
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isStreamingRef = useRef(isGenerating);
  isStreamingRef.current = isGenerating;
  const editorRef = useRef(editor);
  editorRef.current = editor;

  // Expose insertImage to parent
  useImperativeHandle(ref, () => ({
    insertImage: (url: string, alt?: string) => {
      editorRef.current?.chain().focus().setImage({ src: url, alt: alt || '' }).run();
    },
  }), []);

  // Auto-scroll to bottom during streaming — fires on editor content changes
  useEffect(() => {
    if (!editor) return;
    const handle = () => {
      if (!isStreamingRef.current) return;
      // Double rAF to wait for browser layout after content change
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const el = containerRef.current?.parentElement;
          if (el) {
            el.scrollTop = el.scrollHeight;
          }
        });
      });
    };
    editor.on('update', handle);
    return () => { editor.off('update', handle); };
  }, [editor]);

  // Sync external blocks → editor content
  useEffect(() => {
    if (!editor) return;
    let html = blocks.length > 0 ? blocksToHtml(blocks) : '<p></p>';
    // Resolve image URLs
    html = html.replace(/<img\s+src="([^"]+)"/g, (_m: string, src: string) => {
      return `<img src="${resolveImageUrl(src)}"`;
    });
    const currentHtml = editor.getHTML();
    if (html !== currentHtml) {
      // DEBUG: 打印将要 setContent 的 HTML，排查换行来源
      // eslint-disable-next-line no-console
      console.log('[TipTapEditor] setContent HTML:', html);
      editor.commands.setContent(html);
      const schedule = () => {
        injectBlockIds(editor.view.dom, blocks);
        // Auto-scroll during streaming — use double rAF to ensure layout complete
        if (isStreamingRef.current) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              const el = containerRef.current?.parentElement;
              if (el) {
                el.scrollTop = el.scrollHeight;
              }
            });
          });
        }
      };
      if (typeof requestAnimationFrame !== 'undefined') {
        requestAnimationFrame(() => {
          injectTimerRef.current = setTimeout(schedule, 50);
        });
      } else {
        injectTimerRef.current = setTimeout(schedule, 150);
      }
    }
    return () => {
      if (injectTimerRef.current !== null) {
        clearTimeout(injectTimerRef.current);
        injectTimerRef.current = null;
      }
    };
  }, [editor, blocks]);

  // Update editability
  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!readOnly && !isGenerating);
  }, [editor, readOnly, isGenerating]);

  if (!editor) return null;

  return (
    <div
      ref={containerRef}
      className="tiptap-editor-container relative flex-1 overflow-auto bg-[#e8ecf1]"
    >
      <div className="mx-auto my-6 bg-white shadow-lg rounded-sm" style={{ maxWidth: '900px', width: '100%', boxSizing: 'border-box' }}>
        {/* ── Formatting toolbar ── */}
        {editor && !readOnly && (
          <div className="sticky top-0 z-10 flex items-center gap-0.5 px-3 py-1.5 border-b border-gray-200 bg-gray-50 rounded-t-sm flex-wrap">
            {/* Font family */}
            <select
              onChange={(e) => editor.chain().focus().setFontFamily(e.target.value).run()}
              className="text-xs border border-gray-300 rounded px-1 py-0.5 bg-white min-w-[100px]"
              value={(editor.getAttributes('textStyle').fontFamily || '') as string}
            >
              <option value="">字体</option>
              <option value="SimSun">宋体</option>
              <option value="FangSong">仿宋</option>
              <option value="KaiTi">楷体</option>
              <option value="Microsoft YaHei">微软雅黑</option>
              <option value="SimHei">黑体</option>
              <option value="Arial">Arial</option>
            </select>
            <span className="w-px h-5 bg-gray-300 mx-0.5" />
            {/* Bold/Italic/Underline/Strike */}
            <button onClick={() => editor.chain().focus().toggleBold().run()}
              className={`px-1.5 py-0.5 text-xs rounded hover:bg-gray-200 font-bold ${editor.isActive('bold') ? 'bg-gray-300' : ''}`}>B</button>
            <button onClick={() => editor.chain().focus().toggleItalic().run()}
              className={`px-1.5 py-0.5 text-xs rounded hover:bg-gray-200 italic ${editor.isActive('italic') ? 'bg-gray-300' : ''}`}><em>I</em></button>
            <button onClick={() => editor.chain().focus().toggleUnderline().run()}
              className={`px-1.5 py-0.5 text-xs rounded hover:bg-gray-200 underline ${editor.isActive('underline') ? 'bg-gray-300' : ''}`}>U</button>
            <button onClick={() => editor.chain().focus().toggleStrike().run()}
              className={`px-1.5 py-0.5 text-xs rounded hover:bg-gray-200 line-through ${editor.isActive('strike') ? 'bg-gray-300' : ''}`}>S</button>
            <span className="w-px h-5 bg-gray-300 mx-0.5" />
            {/* Color */}
            <input type="color" onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
              className="w-5 h-5 rounded cursor-pointer border-0 p-0" title="文字颜色" />
            <button onClick={() => editor.chain().focus().toggleHighlight().run()}
              className={`px-1.5 py-0.5 text-xs rounded hover:bg-gray-200 ${editor.isActive('highlight') ? 'bg-gray-300' : ''}`} title="高亮">A̲</button>
            <span className="w-px h-5 bg-gray-300 mx-0.5" />
            {/* Lists */}
            <button onClick={() => editor.chain().focus().toggleBulletList().run()}
              className={`px-1.5 py-0.5 text-xs rounded hover:bg-gray-200 ${editor.isActive('bulletList') ? 'bg-gray-300' : ''}`}>•≡</button>
            <button onClick={() => editor.chain().focus().toggleOrderedList().run()}
              className={`px-1.5 py-0.5 text-xs rounded hover:bg-gray-200 ${editor.isActive('orderedList') ? 'bg-gray-300' : ''}`}>1.</button>
            <span className="w-px h-5 bg-gray-300 mx-0.5" />
            {/* Alignment */}
            <button onClick={() => editor.chain().focus().setTextAlign('left').run()}
              className={`px-1.5 py-0.5 text-xs rounded hover:bg-gray-200 ${editor.isActive({ textAlign: 'left' }) ? 'bg-gray-300' : ''}`}>⇤</button>
            <button onClick={() => editor.chain().focus().setTextAlign('center').run()}
              className={`px-1.5 py-0.5 text-xs rounded hover:bg-gray-200 ${editor.isActive({ textAlign: 'center' }) ? 'bg-gray-300' : ''}`}>⇔</button>
            <button onClick={() => editor.chain().focus().setTextAlign('right').run()}
              className={`px-1.5 py-0.5 text-xs rounded hover:bg-gray-200 ${editor.isActive({ textAlign: 'right' }) ? 'bg-gray-300' : ''}`}>⇥</button>
            <button onClick={() => editor.chain().focus().setTextAlign('justify').run()}
              className={`px-1.5 py-0.5 text-xs rounded hover:bg-gray-200 ${editor.isActive({ textAlign: 'justify' }) ? 'bg-gray-300' : ''}`}>□</button>
            <span className="w-px h-5 bg-gray-300 mx-0.5" />
            {/* Quote + Link */}
            <button onClick={() => editor.chain().focus().toggleBlockquote().run()}
              className={`px-1.5 py-0.5 text-xs rounded hover:bg-gray-200 ${editor.isActive('blockquote') ? 'bg-gray-300' : ''}`}>❝</button>
            <button onClick={() => {
              const url = window.prompt('输入链接 URL:');
              if (url) editor.chain().focus().setLink({ href: url }).run();
              else editor.chain().focus().unsetLink().run();
            }}
              className={`px-1.5 py-0.5 text-xs rounded hover:bg-gray-200 ${editor.isActive('link') ? 'bg-blue-100' : ''}`}>🔗</button>
            <button onClick={() => onImageInsert?.()}
              className="px-1.5 py-0.5 text-xs rounded hover:bg-gray-200" title="插入图片">🖼</button>
            <button onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
              className="px-1.5 py-0.5 text-xs rounded hover:bg-gray-200" title="插入表格">⊞</button>
            {editor.isActive('table') && (
              <>
                <span className="w-px h-5 bg-gray-300 mx-0.5" />
                <button onClick={() => editor.chain().focus().addRowBefore().run()}
                  className="px-1.5 py-0.5 text-xs rounded hover:bg-gray-200" title="上方插入行">↑行</button>
                <button onClick={() => editor.chain().focus().addRowAfter().run()}
                  className="px-1.5 py-0.5 text-xs rounded hover:bg-gray-200" title="下方插入行">↓行</button>
                <button onClick={() => editor.chain().focus().deleteRow().run()}
                  className="px-1.5 py-0.5 text-xs rounded hover:bg-red-100 text-red-600" title="删除行">−行</button>
                <button onClick={() => editor.chain().focus().addColumnBefore().run()}
                  className="px-1.5 py-0.5 text-xs rounded hover:bg-gray-200" title="左侧插入列">←列</button>
                <button onClick={() => editor.chain().focus().addColumnAfter().run()}
                  className="px-1.5 py-0.5 text-xs rounded hover:bg-gray-200" title="右侧插入列">→列</button>
                <button onClick={() => editor.chain().focus().deleteColumn().run()}
                  className="px-1.5 py-0.5 text-xs rounded hover:bg-red-100 text-red-600" title="删除列">−列</button>
                <button onClick={() => editor.chain().focus().deleteTable().run()}
                  className="px-1.5 py-0.5 text-xs rounded hover:bg-red-100 text-red-600" title="删除表格">✕表</button>
              </>
            )}
            <span className="w-px h-5 bg-gray-300 mx-0.5" />
            {/* Undo/Redo */}
            <button onClick={() => editor.chain().focus().undo().run()}
              className="px-1.5 py-0.5 text-xs rounded hover:bg-gray-200">↩</button>
            <button onClick={() => editor.chain().focus().redo().run()}
              className="px-1.5 py-0.5 text-xs rounded hover:bg-gray-200">↪</button>
          </div>
        )}

        {/* ── Editor content ── */}
        <div className="min-h-[600px]" style={{ padding: '40pt 56pt', boxSizing: 'border-box' }}>
          <style>{`
            .tiptap-editor-container .ProseMirror { outline: none !important; width: 100% !important; max-width: 100% !important; min-height: 400px; font-size: 12pt; line-height: 1.8; color: #1e293b; white-space: normal !important; word-break: normal !important; overflow-wrap: normal !important; display: block !important; font-family: '宋体', SimSun, 'FangSong', serif !important; }
            .tiptap-editor-container .ProseMirror p { margin: 0 0 10px 0 !important; text-indent: 2em !important; white-space: normal !important; }
            .tiptap-editor-container .ProseMirror h1, .tiptap-editor-container .ProseMirror h2, .tiptap-editor-container .ProseMirror h3 { text-indent: 0 !important; }
            .tiptap-editor-container .ProseMirror h1 { font-size: 18pt; font-weight: 700; margin: 24px 0 12px; color: #0f172a; }
            .tiptap-editor-container .ProseMirror h2 { font-size: 16pt; font-weight: 700; margin: 20px 0 10px; color: #1e293b; }
            .tiptap-editor-container .ProseMirror h3 { font-size: 14pt; font-weight: 600; margin: 16px 0 8px; color: #334155; }
            .tiptap-editor-container .ProseMirror img { max-width: 100%; height: auto; margin: 12px auto; border-radius: 4px; display: block; }
            .tiptap-editor-container .ProseMirror table { border-collapse: collapse; width: 100%; margin: 12px 0; font-size: 11pt; }
            .tiptap-editor-container .ProseMirror th, .tiptap-editor-container .ProseMirror td { border: 1px solid #e2e8f0; padding: 8px 12px; text-align: left; }
            .tiptap-editor-container .ProseMirror th { background: #f8fafc; font-weight: 600; }
            .tiptap-editor-container .ProseMirror ul, .tiptap-editor-container .ProseMirror ol { text-indent: 0 !important; padding-left: 2em; }
            .tiptap-editor-container .ProseMirror blockquote { text-indent: 0 !important; padding-left: 1em; border-left: 3px solid #cbd5e1; color: #475569; }
          `}</style>
          <EditorContent editor={editor} />
        </div>
      </div>
    </div>
  );
});

TipTapEditor.displayName = 'TipTapEditor';
export default TipTapEditor;
